import React from 'react';
const { useState } = React;
import { inputSt, textareaSt, labelSt, cardSt, mutedSt, badgeSt, fmtDate } from '../../diagnostic/shared.jsx';
import { DEFAULT_BRAND_PROFILE, BRAND_COLOR_ROLES, BRAND_FONTS, BRAND_TEXT_LIMITS as BL, HEX_RE, contrastMatrix, fontStack, AA_NORMAL, AA_LARGE } from '../../../brandConfig.js';
import { ownerCall, Notice } from '../shared.jsx';

// The brand profile: positioning, voice, palette and fonts. Saving creates a
// new version; the earlier ones stay as they were, and each piece keeps the
// version it was made with. The contrast of every pair of colors is computed
// here and in the pieces (never guessed).

const linesOf = (t) => String(t || '').split('\n').map(x => x.trim()).filter(Boolean);
const roleLabel = (k) => BRAND_COLOR_ROLES.find(r => r.key === k)?.label || k;

function ListField({ label, hint, value, onChange }) {
  return (
    <label style={{ display: 'block' }}><span style={labelSt}>{label}</span>
      <textarea style={{ ...textareaSt, minHeight: 90 }} value={value} onChange={e => onChange(e.target.value)} />
      <span style={mutedSt}>{hint || 'Una por línea.'}</span>
    </label>
  );
}

export function ContrastTable({ palette }) {
  const valid = BRAND_COLOR_ROLES.every(r => HEX_RE.test(palette[r.key] || ''));
  if (!valid) return <div style={mutedSt}>Completa los cuatro colores para ver el contraste.</div>;
  const rows = contrastMatrix(palette);
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ borderCollapse: 'collapse', fontSize: 12, width: '100%', minWidth: 420 }}>
        <caption style={{ ...mutedSt, textAlign: 'left', marginBottom: 6 }}>Contraste de cada texto sobre cada fondo (WCAG). Texto normal: {AA_NORMAL}:1. Texto grande (títulos): {AA_LARGE}:1.</caption>
        <thead><tr>{['Muestra', 'Texto', 'Fondo', 'Contraste', 'Uso'].map(h => <th key={h} scope="col" style={{ textAlign: 'left', padding: '4px 6px', borderBottom: '1px solid var(--globant-border)' }}>{h}</th>)}</tr></thead>
        <tbody>
          {rows.map(r => (
            <tr key={`${r.fg}-${r.bg}`}>
              <td style={{ padding: '4px 6px' }}><span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 4, background: palette[r.bg], color: palette[r.fg], fontFamily: fontStack('Georgia'), fontWeight: 700 }}>Aa</span></td>
              <td style={{ padding: '4px 6px' }}>{roleLabel(r.fg)}</td>
              <td style={{ padding: '4px 6px' }}>{roleLabel(r.bg)}</td>
              <td style={{ padding: '4px 6px', fontVariantNumeric: 'tabular-nums' }}>{r.ratio.toFixed(2)}:1</td>
              <td style={{ padding: '4px 6px' }}>{r.normal ? <span style={badgeSt(true)}>Cualquier texto</span> : r.large ? <span style={{ ...badgeSt(false), color: '#9A6212' }}>Solo títulos grandes</span> : <span style={{ ...badgeSt(false), color: '#B4362C' }}>No para texto: solo detalles</span>}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function BrandProfile({ current, versions, onSaved }) {
  const p = current?.profile || DEFAULT_BRAND_PROFILE;
  const [v, setV] = useState(() => ({
    name: p?.name || '', positioning: p?.positioning || '', audience: p?.audience || '',
    values: (p?.values || []).join('\n'), voice: (p?.voice || []).join('\n'), avoid: (p?.avoid || []).join('\n'), visual: (p?.visual || []).join('\n'),
    palette: { ...(p?.palette || {}) }, fonts: { ...(p?.fonts || { title: 'Georgia', body: 'Arial' }) }, note: '',
  }));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState('');
  const set = (k) => (val) => setV(x => ({ ...x, [k]: val }));
  const save = async () => {
    setBusy(true); setError(''); setSaved('');
    try {
      const profile = { name: v.name, positioning: v.positioning, audience: v.audience, values: linesOf(v.values), voice: linesOf(v.voice), avoid: linesOf(v.avoid), visual: linesOf(v.visual), palette: v.palette, fonts: v.fonts };
      const { version } = await ownerCall({ action: 'brandSaveVersion', profile, note: v.note });
      setSaved(`Guardada como versión ${version.version}. Las piezas anteriores conservan su versión.`);
      setV(x => ({ ...x, note: '' }));
      onSaved?.(version);
    } catch (e) { setError(e.message); }
    setBusy(false);
  };

  return (
    <div>
      <Notice icon="🎨" tone="info">
        Esta identidad se aplica <b>solo a las piezas de tu marca personal</b>. Oike conserva su diseño y sus colores; las propuestas comerciales, los formularios y las demás pantallas no cambian.
      </Notice>
      {current ? <div style={{ ...mutedSt, margin: '8px 0' }}>Estás viendo la versión {current.version} (guardada el {fmtDate(current.createdAt)}). Guardar crea la versión {current.version + 1}; la anterior no cambia.</div>
        : <div style={{ ...mutedSt, margin: '8px 0' }}>Todavía no guardaste tu perfil: esta es la identidad aprobada como base. Guárdala como versión 1 para crear piezas.</div>}
      <div style={cardSt}>
        <h3 style={{ margin: '0 0 10px', fontSize: 16 }}>Posicionamiento y voz</h3>
        <div style={{ display: 'grid', gap: 12 }}>
          <label><span style={labelSt}>Nombre de la marca</span><input style={inputSt} value={v.name} maxLength={BL.name} onChange={e => set('name')(e.target.value)} /></label>
          <label><span style={labelSt}>Posicionamiento</span><textarea style={{ ...textareaSt, minHeight: 90 }} value={v.positioning} maxLength={BL.positioning} onChange={e => set('positioning')(e.target.value)} /></label>
          <label><span style={labelSt}>Para quién</span><textarea style={{ ...textareaSt, minHeight: 70 }} value={v.audience} maxLength={BL.audience} onChange={e => set('audience')(e.target.value)} /></label>
          <ListField label="Valores" value={v.values} onChange={set('values')} />
          <ListField label="Voz" hint="Cómo escribes. Una regla por línea." value={v.voice} onChange={set('voice')} />
          <ListField label="Evitar" hint="Lo que la IA y tú evitan en cada pieza. Una por línea." value={v.avoid} onChange={set('avoid')} />
        </div>
      </div>
      <div style={cardSt}>
        <h3 style={{ margin: '0 0 10px', fontSize: 16 }}>Paleta y tipografías</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: 12 }}>
          {BRAND_COLOR_ROLES.map(r => {
            const c = v.palette[r.key] || '';
            const okHex = HEX_RE.test(c);
            return (
              <div key={r.key} style={{ border: '1px solid var(--globant-border)', borderRadius: 10, overflow: 'hidden' }}>
                <div aria-hidden="true" style={{ height: 46, background: okHex ? c : 'repeating-linear-gradient(45deg,#eee,#eee 6px,#fff 6px,#fff 12px)' }} />
                <div style={{ padding: 8, display: 'grid', gap: 4 }}>
                  <label htmlFor={`brand-color-${r.key}`} style={{ fontWeight: 700, fontSize: 13 }}>{r.label}</label>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <input id={`brand-color-${r.key}`} style={{ ...inputSt, fontFamily: 'monospace' }} value={c} maxLength={7} aria-invalid={!okHex}
                      onChange={e => setV(x => ({ ...x, palette: { ...x.palette, [r.key]: e.target.value.trim() } }))} />
                    <input type="color" aria-label={`Elegir ${r.label}`} value={okHex ? c : '#000000'} style={{ width: 44, minHeight: 36, padding: 0, border: '1px solid var(--globant-border)', borderRadius: 6 }}
                      onChange={e => setV(x => ({ ...x, palette: { ...x.palette, [r.key]: e.target.value.toUpperCase() } }))} />
                  </div>
                  <span style={mutedSt}>{r.use}</span>
                </div>
              </div>
            );
          })}
        </div>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 12 }}>
          {[['title', 'Títulos editoriales'], ['body', 'Textos']].map(([k, label]) => (
            <label key={k} style={{ flex: '1 1 200px' }}><span style={labelSt}>{label}</span>
              <select style={inputSt} value={v.fonts[k]} onChange={e => setV(x => ({ ...x, fonts: { ...x.fonts, [k]: e.target.value } }))}>
                {BRAND_FONTS.map(f => <option key={f} value={f}>{f}</option>)}
              </select>
              <span style={{ display: 'block', fontFamily: fontStack(v.fonts[k]), fontSize: k === 'title' ? 22 : 15, marginTop: 6 }}>{k === 'title' ? 'Lo que traje en la valija' : 'Una acción concreta para esta semana.'}</span>
            </label>
          ))}
        </div>
        <p style={{ ...mutedSt, marginBottom: 0 }}>Georgia y Arial vienen instaladas en Mac y Windows: el PNG se arma con las fuentes de tu computadora.</p>
        <div style={{ marginTop: 12 }}><ContrastTable palette={v.palette} /></div>
        <div style={{ marginTop: 12 }}><ListField label="Reglas visuales" value={v.visual} onChange={set('visual')} /></div>
      </div>
      <div style={cardSt}>
        <label><span style={labelSt}>Nota de esta versión (opcional)</span><input style={inputSt} value={v.note} maxLength={BL.note} placeholder="Qué cambiaste" onChange={e => set('note')(e.target.value)} /></label>
        {error && <div role="alert" style={{ color: '#B4362C', fontSize: 13, marginTop: 8 }}>{error}</div>}
        {saved && <div role="status" style={{ color: '#1E7B4F', fontSize: 13, marginTop: 8 }}>{saved}</div>}
        <div style={{ marginTop: 10 }}><button className="action-btn btn-primary" disabled={busy} onClick={save}>{busy ? 'Guardando…' : current ? `Guardar como versión ${current.version + 1}` : 'Guardar como versión 1'}</button></div>
      </div>
      {versions?.length > 0 && (
        <div style={cardSt}>
          <h3 style={{ margin: '0 0 8px', fontSize: 16 }}>Versiones</h3>
          {versions.map(x => <div key={x.id} style={{ fontSize: 13, margin: '4px 0' }}><b>Versión {x.version}</b> <span style={mutedSt}>· {fmtDate(x.createdAt)}{x.note ? ` · ${x.note}` : ''}</span></div>)}
        </div>
      )}
    </div>
  );
}
